/**
 * Resolve EOS account name from public key.
 * Hyperion get_key_accounts first, then get_accounts_by_authorizers.
 * Keep the endpoint list short — long lists + get_info probes caused CORS storms in the browser.
 */

import { APIClient } from '@wharfkit/antelope'
import { HyperionAPIClient } from '@wharfkit/hyperion'

/** Endpoints that usually allow browser CORS for Hyperion / chain APIs. */
// eos.eosrio.io dropped 2026-08-27 — DNS no longer resolves (see chain-eos/config.ts),
// was only adding ERR_NAME_NOT_RESOLVED noise + latency before falling through.
const DEFAULT_ENDPOINTS = [
  'https://eos.eosusa.io',
  'https://eos.hyperion.eosrio.io',
  'https://eos.greymass.com',
] as const

/** Preferred endpoints set by the app (selected RPC goes first). */
let preferredEndpoints: string[] = []

export function setPreferredResolveEndpoints(urls: string[]): void {
  preferredEndpoints = [...new Set(urls.map(u => u.trim().replace(/\/$/, '')).filter(Boolean))]
}

export function getPreferredResolveEndpoints(): string[] {
  return [...preferredEndpoints]
}

export const RESOLVE_ERROR_NETWORK = 'RESOLVE_NETWORK'
export const RESOLVE_ERROR_ACCOUNT_NOT_FOUND = 'RESOLVE_ACCOUNT_NOT_FOUND'

function uniqUrls(...lists: Array<Iterable<string>>): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const list of lists) {
    for (const raw of list) {
      const u = raw.trim().replace(/\/$/, '')
      if (!u || seen.has(u)) continue
      seen.add(u)
      out.push(u)
    }
  }
  return out
}

async function resolveViaHyperion(endpoint: string, keys: string[]): Promise<string | null> {
  const api = new APIClient({ url: endpoint })
  const hyperion = new HyperionAPIClient(api)
  for (const publicKey of keys) {
    try {
      const result = await hyperion.v2.state.get_key_accounts(publicKey)
      const names = result.account_names
      if (names && names.length > 0) return String(names[0])
    } catch {
      /* try next key */
    }
  }
  return null
}

async function resolveViaAuthorizers(endpoint: string, keys: string[]): Promise<string | null> {
  try {
    const res = await fetch(`${endpoint}/v1/chain/get_accounts_by_authorizers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accounts: [], keys }),
    })
    if (!res.ok) return null
    const body = (await res.json()) as {
      accounts?: Array<{ account_name?: string }>
    }
    const name = body.accounts?.find(a => a.account_name)?.account_name
    return name ? String(name) : null
  } catch {
    return null
  }
}

export async function fetchAccountNameByPublicKey(publicKeyOrKeys: string | string[]): Promise<string> {
  const keys = Array.isArray(publicKeyOrKeys) ? publicKeyOrKeys : [publicKeyOrKeys]
  const uniqueKeys = [...new Set(keys.filter(Boolean))]

  // Preferred first, then a short CORS-friendly default list (cap to avoid storms)
  const endpoints = uniqUrls(preferredEndpoints, DEFAULT_ENDPOINTS).slice(0, 6)

  let reachedServer = false

  for (const endpoint of endpoints) {
    try {
      const name = await resolveViaHyperion(endpoint, uniqueKeys)
      reachedServer = true
      if (name) return name
    } catch {
      /* continue */
    }
  }

  for (const endpoint of endpoints) {
    const name = await resolveViaAuthorizers(endpoint, uniqueKeys)
    if (name) return name
    // Successful empty JSON still means we reached a server
    reachedServer = true
  }

  if (!reachedServer) {
    throw new Error(RESOLVE_ERROR_NETWORK)
  }
  throw new Error(RESOLVE_ERROR_ACCOUNT_NOT_FOUND)
}
